import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Textarea } from '~/components/ui/textarea';
import { apiClient } from '~/lib/api';

export default function FeaturedPage() {
  const queryClient = useQueryClient();
  const [featuredCasts, setFeaturedCasts] = useState('');
  const [featuredStudios, setFeaturedStudios] = useState('');
  const [featuredCategories, setFeaturedCategories] = useState('');
  const [featuredVideos, setFeaturedVideos] = useState('');
  const [trendingKeywords, setTrendingKeywords] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-featured'],
    queryFn: () => apiClient.getFeaturedContent(),
  });

  useEffect(() => {
    if (data?.data) {
      const content = data.data as any;
      setFeaturedCasts(Array.isArray(content.featuredCasts) ? content.featuredCasts.join('\n') : '');
      setFeaturedStudios(Array.isArray(content.featuredStudios) ? content.featuredStudios.join('\n') : '');
      setFeaturedCategories(Array.isArray(content.featuredCategories) ? content.featuredCategories.join('\n') : '');
      setFeaturedVideos(Array.isArray(content.featuredVideos) ? content.featuredVideos.join('\n') : '');
      setTrendingKeywords(Array.isArray(content.trendingKeywords) ? content.trendingKeywords.join('\n') : '');
    }
  }, [data]);

  const updateFeaturedMutation = useMutation({
    mutationFn: (data: {
      featuredCasts?: string[];
      featuredStudios?: string[];
      featuredCategories?: string[];
      featuredVideos?: string[];
      trendingKeywords?: string[];
    }) => apiClient.updateFeaturedContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-featured'] });
    },
  });

  const handleSave = () => {
    const castsArray = featuredCasts
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const studiosArray = featuredStudios
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const categoriesArray = featuredCategories
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const videosArray = featuredVideos
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);
    const keywordsArray = trendingKeywords
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

    updateFeaturedMutation.mutate({
      featuredCasts: castsArray,
      featuredStudios: studiosArray,
      featuredCategories: categoriesArray,
      featuredVideos: videosArray,
      trendingKeywords: keywordsArray,
    });
  };

  if (isLoading) {
    return (
      <div className='space-y-6'>
        <div>
          <h1 className='text-3xl font-bold'>Featured Content</h1>
          <p className='text-muted-foreground'>Manage featured idols, studios, and categories</p>
        </div>
        <div className='text-center py-8'>
          <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4'></div>
          <p className='text-muted-foreground'>Loading featured content...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Featured Content</h1>
          <p className='text-muted-foreground'>Manage featured idols, studios, and categories</p>
        </div>
        <Button onClick={handleSave} disabled={updateFeaturedMutation.isPending}>
          <Save className='h-4 w-4 mr-2' />
          {updateFeaturedMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {updateFeaturedMutation.isSuccess && (
        <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4'>
          <p className='text-sm text-green-800 dark:text-green-500'>Featured content updated successfully!</p>
        </div>
      )}

      {updateFeaturedMutation.isError && (
        <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
          <p className='text-sm text-red-800 dark:text-red-500'>Failed to update featured content. Please try again.</p>
        </div>
      )}

      {/* Trending Keywords Section */}
      <Card className='mb-6'>
        <CardHeader>
          <CardTitle>🔥 Trending Keywords</CardTitle>
          <CardDescription>Enter trending search keywords for homepage hero section (one per line)</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder='sone&#10;seto kanna&#10;madonna&#10;nagi hikaru&#10;sayuri hayama&#10;faleno&#10;cawd&#10;jur'
            value={trendingKeywords}
            onChange={(e) => setTrendingKeywords(e.target.value)}
            rows={8}
            className='font-mono text-sm'
          />
          <p className='text-xs text-muted-foreground mt-2'>
            Currently {trendingKeywords.split('\n').filter(Boolean).length} keywords
          </p>
        </CardContent>
      </Card>

      <div className='grid gap-6 md:grid-cols-2 lg:grid-cols-4'>
        {/* Featured Idols */}
        <Card>
          <CardHeader>
            <CardTitle>Featured Idols</CardTitle>
            <CardDescription>Enter idol slugs (one per line)</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder='yui-hatano&#10;arina-hashimoto&#10;eimi-fukada'
              value={featuredCasts}
              onChange={(e) => setFeaturedCasts(e.target.value)}
              rows={15}
              className='font-mono text-sm'
            />
            <p className='text-xs text-muted-foreground mt-2'>
              Currently {featuredCasts.split('\n').filter(Boolean).length} idols
            </p>
          </CardContent>
        </Card>

        {/* Featured Studios */}
        <Card>
          <CardHeader>
            <CardTitle>Featured Studios</CardTitle>
            <CardDescription>Enter studio slugs (one per line)</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder='s1-no-1-style&#10;moodyz&#10;ideapocket'
              value={featuredStudios}
              onChange={(e) => setFeaturedStudios(e.target.value)}
              rows={15}
              className='font-mono text-sm'
            />
            <p className='text-xs text-muted-foreground mt-2'>
              Currently {featuredStudios.split('\n').filter(Boolean).length} studios
            </p>
          </CardContent>
        </Card>

        {/* Featured Categories */}
        <Card>
          <CardHeader>
            <CardTitle>Featured Categories</CardTitle>
            <CardDescription>Enter category slugs (one per line)</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder='big-tits&#10;beautiful-girl&#10;planning'
              value={featuredCategories}
              onChange={(e) => setFeaturedCategories(e.target.value)}
              rows={15}
              className='font-mono text-sm'
            />
            <p className='text-xs text-muted-foreground mt-2'>
              Currently {featuredCategories.split('\n').filter(Boolean).length} categories
            </p>
          </CardContent>
        </Card>

        {/* Featured Videos */}
        <Card>
          <CardHeader>
            <CardTitle>Featured Videos</CardTitle>
            <CardDescription>Enter video contentIds (one per line)</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder='1abc123&#10;2def456&#10;3ghi789'
              value={featuredVideos}
              onChange={(e) => setFeaturedVideos(e.target.value)}
              rows={15}
              className='font-mono text-sm'
            />
            <p className='text-xs text-muted-foreground mt-2'>
              Currently {featuredVideos.split('\n').filter(Boolean).length} videos
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Usage Instructions</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div>
            <h4 className='font-semibold mb-2'>Trending Keywords:</h4>
            <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
              <li>
                <strong>Purpose:</strong> Keywords shown in the hero section of the homepage for quick search access
              </li>
              <li>
                <strong>Format:</strong> Can be idol names, studio names, genre names, or popular search terms
              </li>
              <li>
                <strong>Example:</strong> "sone", "seto kanna", "madonna", "cawd", "jur"
              </li>
              <li>
                <strong>Best Practices:</strong> Mix of idol names, studio codes, and popular terms. Keep to 6-10
                keywords.
              </li>
            </ul>
          </div>
          <div>
            <h4 className='font-semibold mb-2'>How to find slugs:</h4>
            <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
              <li>
                <strong>Idols:</strong> Go to javfacts.com/en/idols/[idol-name] - the slug is in the URL (e.g.,
                "yui-hatano")
              </li>
              <li>
                <strong>Studios:</strong> Go to javfacts.com/en/studios/[studio-name] - the slug is in the URL (e.g.,
                "s1-no-1-style")
              </li>
              <li>
                <strong>Categories:</strong> Go to javfacts.com/en/genres/[category-name] - the slug is in the URL
                (e.g., "big-tits")
              </li>
              <li>
                <strong>Videos:</strong> Use contentId (NOT DVD ID) - it's a unique ID for each video. You can find it
                in the database or video API response.
              </li>
            </ul>
          </div>
          <div>
            <h4 className='font-semibold mb-2'>Format:</h4>
            <p className='text-sm text-muted-foreground'>
              Enter one slug per line. The order matters - items will be displayed in the same order on the homepage.
            </p>
          </div>
          <div>
            <h4 className='font-semibold mb-2'>Tips:</h4>
            <ul className='list-disc list-inside space-y-1 text-sm text-muted-foreground'>
              <li>Remove empty lines - they will be ignored</li>
              <li>Make sure slugs are correct - invalid slugs won't display</li>
              <li>Featured content updates immediately after saving</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
